import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const InterestRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { toast } = useToast();
  const ranRef = useRef(false);

  useEffect(() => {
    if (loading || ranRef.current || !slug) return;

    if (!isAuthenticated) {
      navigate(`/auth?redirectTo=/interest/${slug}`, { replace: true });
      return;
    }

    ranRef.current = true;

    (async () => {
      try {
        const { data: section, error: secErr } = await supabase
          .from("experience_sections")
          .select("id, title, slug, enable_interest_signup")
          .eq("slug", slug)
          .maybeSingle();

        if (secErr || !section) {
          toast({
            title: "Series not found",
            description: "We couldn't find that series.",
            variant: "destructive",
          });
          navigate("/events", { replace: true });
          return;
        }

        if (!section.enable_interest_signup) {
          toast({
            title: "Sign-ups closed",
            description: `${section.title} is not currently accepting interest sign-ups.`,
          });
          navigate("/events", { replace: true });
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate(`/auth?redirectTo=/interest/${slug}`, { replace: true });
          return;
        }

        const { error: insErr } = await supabase
          .from("series_interest")
          .insert({ user_id: user.id, section_id: section.id });

        const isDuplicate =
          insErr && String(insErr.message).toLowerCase().includes("duplicate");

        if (insErr && !isDuplicate) {
          toast({
            title: "Something went wrong",
            description: insErr.message,
            variant: "destructive",
          });
          navigate("/events", { replace: true });
          return;
        }

        toast({
          title: isDuplicate ? "You're already on the list" : "You're on the list",
          description: `We'll email you when new ${section.title} events launch.`,
        });
        navigate("/events", { replace: true });
      } catch (e: any) {
        toast({
          title: "Something went wrong",
          description: e?.message ?? "Please try again.",
          variant: "destructive",
        });
        navigate("/events", { replace: true });
      }
    })();
  }, [slug, isAuthenticated, loading, navigate, toast]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <LoadingSpinner />
    </div>
  );
};

export default InterestRedirect;
